import { Content } from "@/services/content/types";
import { getColor } from "@/utils/articles";
import classNames from "classnames";
import Link from "next/link";
import TeaserLinkedContent from "../teaser-linked-content";

type LinkedContentProps = {
  color?: string;
  linkedContent?: Content.Entry[];
  children: React.ReactNode;
  highlighted?: string;
  darkMode?: boolean;
  showLinkedContent?: boolean;
  bigLargeArticle?: boolean;
  showLeftLine?: boolean;
  compactMode?: boolean;
  category?: Content.Category;
  teaserLayoutType?: string;
  forceBoxed?: boolean;
};

const LinkedContent = (props: LinkedContentProps) => {
  const hasLinkedContent = Boolean(props.linkedContent?.length);
  const isSideBySide = props.teaserLayoutType === "1 stor - halvor";

  if (
    !props.forceBoxed &&
    (!hasLinkedContent || props.showLinkedContent === false)
  ) {
    return props.children;
  }

  if (isSideBySide) {
    const linkedItems = props.linkedContent || [];
    const isOdd = linkedItems.length % 2 !== 0;
    const pairedItems = isOdd ? linkedItems.slice(0, -1) : linkedItems;
    const lastItem = isOdd ? linkedItems[linkedItems.length - 1] : null;

    const borderColor =
      props.highlighted === "Nivå 1(röd)"
        ? "border-red-950"
        : props.highlighted === "Nivå 2(beige)"
          ? "border-sand-900"
          : "border-sand-700";

    return (
      <div
        className={classNames("-ml-4 relative pl-2 pb-4", {
          "bg-red-900 pl-6 pr-4 w-[calc(100vw-0px)] md:w-auto md:!ml-0 ":
            props.highlighted === "Nivå 1(röd)",
          "bg-sand-700 pl-6 pr-4 w-[calc(100vw-0px)] md:w-auto md:!ml-0  ":
            props.highlighted === "Nivå 2(beige)",
        })}
      >
        {props.category?.fields?.name && (
          <Link href={`/amne/${props.category.fields.slug}`} className="block">
            <span
              className={classNames("block font-bold uppercase font-sans mb-3 pt-4", {
                "text-static-white": props.highlighted === "Nivå 1(röd)",
              })}
            >
              {props.category.fields.name}
            </span>
          </Link>
        )}
        <div
          className={classNames(
            "absolute -left-[8px] inset-y-0 w-[8px] h-full ",
            getColor(props.color),
            {
              "!bg-red-950 !left-0": props.highlighted === "Nivå 1(röd)",
              "!bg-sand-900 !left-0": props.highlighted === "Nivå 2(beige)",
            },
          )}
        />
        {props.children}

        <div className="mt-0 md:grid md:grid-cols-2">
          {pairedItems.map((item, index) => (
            <div
              key={item.sys.id}
              className={classNames(
                "border-b pb-4 mb-4",
                borderColor,
                index % 2 === 0 ? "md:border-r md:pr-4" : "md:pl-4",
              )}
            >
              <TeaserLinkedContent
                linkedContent={[item]}
                showLinkedContent={true}
                highlighted={props.highlighted}
                compactMode={props.compactMode || false}
                darkMode={false}
                bigLargeArticle={props.bigLargeArticle}
                teaserLayoutType={props.teaserLayoutType}
              />
            </div>
          ))}
        </div>
        {lastItem && (
          <div>
            <TeaserLinkedContent
              linkedContent={[lastItem]}
              showLinkedContent={true}
              highlighted={props.highlighted}
              compactMode={props.compactMode || false}
              darkMode={false}
              bigLargeArticle={props.bigLargeArticle}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={classNames("-ml-2 relative pl-2 pb-4", {
        "bg-red-900 pl-3 pr-4 w-[calc(100vw-8px)] md:w-auto":
          props.highlighted === "Nivå 1(röd)",
        "bg-sand-700 pl-3 pr-4 w-[calc(100vw-8px)] md:w-auto":
          props.highlighted === "Nivå 2(beige)",
      })}
    >
      {props.category?.fields?.name && props.highlighted && (
        <Link href={`/amne/${props.category.fields.slug}`} className="block">
          <span
            className={classNames("block font-bold uppercase font-sans mb-3 pt-4", {
              "text-static-white": props.highlighted === "Nivå 1(röd)",
            })}
          >
            {props.category.fields.name}
          </span>
        </Link>
      )}
      <div
        className={classNames(
          "absolute -left-[8px] inset-y-0 w-[8px] h-full ",
          getColor(props.color),
          {
            "!bg-red-950": props.highlighted === "Nivå 1(röd)",
            "!bg-sand-900": props.highlighted === "Nivå 2(beige)",
          },
        )}
      />
      {props.children}

      <div className="mt">
        <TeaserLinkedContent
          linkedContent={props.linkedContent}
          showLinkedContent={true}
          highlighted={props.highlighted}
          compactMode={props.compactMode || false}
          darkMode={false}
          bigLargeArticle={props.bigLargeArticle}
        />
      </div>
    </div>
  );
};

export default LinkedContent;
